import openaiBackendService from './openaiBackendService';
import openai from './openaiClient'; // Keep for fallback

// Flag to control whether to use backend or direct OpenAI
const USE_BACKEND = import.meta.env.VITE_USE_BACKEND !== 'false'; // Default to true

/**
 * Analyzes CV content and provides structured scoring and insights
 * Now uses backend API by default, with fallback to direct OpenAI
 * @param {string} cvText - Extracted text from CV
 * @param {string} jobPosition - Target job position
 * @param {string} jobRequirements - Job requirements and criteria
 * @returns {Promise<object>} Analysis results with scoring and insights
 */
export async function analyzeCVWithAI(cvText, jobPosition = "Software Developer", jobRequirements = "") {
  if (USE_BACKEND) {
    try {
      const result = await openaiBackendService.analyzeCv({
        cvText,
        jobPosition,
        jobRequirements,
      });
      
      // Transform backend response to match frontend expectations
      const analysis = result.analysis;
      return {
        candidateName: analysis.candidate_name,
        overallScore: analysis.overall_score,
        skillsMatch: analysis.skills_match,
        experienceMatch: analysis.experience_match,
        educationMatch: analysis.education_match,
        culturalFit: analysis.cultural_fit,
        keySkills: analysis.key_skills,
        keyHighlights: analysis.key_highlights,
        aiSummary: analysis.ai_summary,
        scoringBreakdown: analysis.scoring_breakdown.map(item => ({
          category: item.category,
          score: item.score,
          feedback: item.feedback,
        })),
        recommendations: analysis.recommendations.map(item => ({
          type: item.recommendation_type,
          title: item.title,
          description: item.description,
        })),
        analysisDate: analysis.analysis_date || new Date().toISOString(),
        aiModel: analysis.ai_model || 'gpt-4o',
      };
    } catch (error) {
      console.warn('Backend CV analysis failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  
  // Fallback to direct OpenAI if backend is disabled or failed
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: `You are an expert HR AI system specializing in CV analysis and candidate evaluation. You provide detailed, professional assessments of candidate qualifications against job requirements.`
        },
        { 
          role: 'user', 
          content: `Analyze this CV for the position of "${jobPosition}". Job requirements: ${jobRequirements || "General requirements for the position"}

CV Content:
${cvText}

Please provide a comprehensive analysis.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'cv_analysis_response',
          schema: {
            type: 'object',
            properties: {
              candidateName: { type: 'string' },
              overallScore: { type: 'number' },
              skillsMatch: { type: 'number' },
              experienceMatch: { type: 'number' },
              educationMatch: { type: 'number' },
              culturalFit: { type: 'number' },
              keySkills: { 
                type: 'array',
                items: { type: 'string' }
              },
              keyHighlights: {
                type: 'array',
                items: { type: 'string' }
              },
              aiSummary: { type: 'string' },
              scoringBreakdown: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    category: { type: 'string' },
                    score: { type: 'number' },
                    feedback: { type: 'string' }
                  },
                  required: ['category', 'score', 'feedback']
                }
              },
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string' },
                    title: { type: 'string' },
                    description: { type: 'string' }
                  },
                  required: ['type', 'title', 'description']
                }
              }
            },
            required: [
              'candidateName', 'overallScore', 'skillsMatch', 'experienceMatch', 
              'educationMatch', 'culturalFit', 'keySkills', 'keyHighlights', 
              'aiSummary', 'scoringBreakdown', 'recommendations'
            ],
            additionalProperties: false,
          },
        },
      },
    });

    const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
    
    return {
      ...analysis,
      analysisDate: new Date()?.toISOString(),
      aiModel: 'gpt-4o'
    };
  } catch (error) {
    console.error('Error in CV analysis:', error);
    throw new Error(`CV Analysis failed: ${error.message}`);
  }
}

/**
 * Extracts key information from CV text for quick processing
 * Now uses backend API by default, with fallback to direct OpenAI
 * @param {string} cvText - Raw CV text
 * @returns {Promise<object>} Extracted key information
 */
export async function extractCVKeyInfo(cvText) {
  if (USE_BACKEND) {
    try {
      const result = await openaiBackendService.extractCvInfo(cvText);
      
      // Transform backend response to match frontend expectations
      const info = result.extracted_info;
      return {
        name: info.name,
        email: info.email,
        phone: info.phone,
        location: info.location,
        experience: info.experience,
        topSkills: info.top_skills,
        currentRole: info.current_role,
        education: info.education,
      };
    } catch (error) {
      console.warn('Backend CV extraction failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini',
      messages: [
        { 
          role: 'system', 
          content: 'You are an AI system that extracts key information from CVs quickly and accurately.'
        },
        { 
          role: 'user', 
          content: `Extract key information from this CV:

${cvText}`
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'cv_extraction_response',
          schema: {
            type: 'object',
            properties: {
              name: { type: 'string' },
              email: { type: 'string' },
              phone: { type: 'string' },
              location: { type: 'string' },
              experience: { type: 'string' },
              topSkills: {
                type: 'array',
                items: { type: 'string' }
              },
              currentRole: { type: 'string' },
              education: { type: 'string' }
            },
            required: ['name', 'topSkills'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in CV key info extraction:', error);
    throw new Error(`CV key info extraction failed: ${error.message}`);
  }
}

/**
 * Generates interview questions based on CV analysis
 * Now uses backend API by default, with fallback to direct OpenAI
 * @param {object} candidate - Candidate information
 * @param {string} jobPosition - Target position
 * @returns {Promise<Array>} Array of suggested interview questions
 */
export async function generateInterviewQuestions(candidate, jobPosition = "Software Developer") {
  if (USE_BACKEND) {
    try {
      const result = await openaiBackendService.generateQuestions({
        candidateSummary: candidate?.aiSummary || `Candidate: ${candidate?.name}`,
        jobPosition,
        keySkills: candidate?.keySkills || [],
      });
      
      // Transform backend response to match frontend expectations
      const questions = result.questions;
      return {
        technicalQuestions: questions.technical_questions,
        behavioralQuestions: questions.behavioral_questions,
        roleSpecificQuestions: questions.role_specific_questions,
      };
    } catch (error) {
      console.warn('Backend question generation failed, falling back to direct OpenAI:', error.message);
      // Fallback to direct OpenAI call
    }
  }
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert HR professional who creates thoughtful, relevant interview questions based on candidate profiles and job requirements.'
        },
        { 
          role: 'user', 
          content: `Generate interview questions for this candidate applying for ${jobPosition}:

Candidate: ${candidate?.name}
Skills: ${candidate?.keySkills?.join(', ')}
Experience: ${candidate?.experience}
Key Highlights: ${candidate?.keyHighlights?.join(', ')}
AI Summary: ${candidate?.aiSummary}

Generate a mix of technical, behavioral, and role-specific questions.`
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'interview_questions_response',
          schema: {
            type: 'object',
            properties: {
              technicalQuestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    question: { type: 'string' },
                    category: { type: 'string' },
                    difficulty: { type: 'string' }
                  },
                  required: ['question', 'category', 'difficulty']
                }
              },
              behavioralQuestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    question: { type: 'string' },
                    category: { type: 'string' },
                    purpose: { type: 'string' }
                  },
                  required: ['question', 'category', 'purpose']
                }
              },
              roleSpecificQuestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    question: { type: 'string' },
                    category: { type: 'string' },
                    relevance: { type: 'string' }
                  },
                  required: ['question', 'category', 'relevance']
                }
              }
            },
            required: ['technicalQuestions', 'behavioralQuestions', 'roleSpecificQuestions'],
            additionalProperties: false,
          },
        },
      },
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error generating interview questions:', error);
    throw new Error(`Interview questions generation failed: ${error.message}`);
  }
}
function generateHiringInsights(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: generateHiringInsights is not implemented yet.', args);
  return null;
}

export { generateHiringInsights };